import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SearchableLocationDropdown } from "../../../components";
import { getUserInfo, RootState } from "../../../app";
import { routes } from "../../../app/routes";
import { setLocationPageLocation } from "../../../app/store/calendarPreferencesSlice";
import { CalendarEventModel, LocationProvidersModel, UpdateCalendarEvent } from "../../../data/entities";
import { CalendarEventService, LocationService, UserProviderCalendarService } from "../../../services";
import {
  formatTimeOnly,
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
  toYearMonthDayLocalString,
} from "../../../utils";
import { CalendarBookingModal } from "../../calendar/components";
import { AgendaMode, getEventEnd, getEventStart, useAgendaEvents } from "../hooks";
import { AgendaCard } from "./AgendaCard";

export function AgendaDashboard() {
  const dispatch = useDispatch();
  const currentUserId = useSelector(getUserInfo())?.id;

  const calendarEventService = useMemo(() => new CalendarEventService(), []);
  const locationService = useMemo(() => new LocationService(), []);
  const userProviderCalendarService = useMemo(() => new UserProviderCalendarService(), []);

  const cachedLocationId = useSelector((state: RootState) =>
    currentUserId
      ? state.calendarPreferences.locationPreferences[currentUserId.toString()]?.selectedLocationId
      : undefined
  );

  const [isInitialized, setIsInitialized] = useState(false);
  const [isProvider, setIsProvider] = useState(false);
  const [accessibleLocations, setAccessibleLocations] = useState<LocationProvidersModel[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");

  const [myMode, setMyMode] = useState<AgendaMode>("upcoming");
  const [myDate, setMyDate] = useState(() => new Date());
  const [locationMode, setLocationMode] = useState<AgendaMode>("upcoming");
  const [locationDate, setLocationDate] = useState(() => new Date());

  const [editingEvent, setEditingEvent] = useState<CalendarEventModel | null>(null);

  useEffect(() => {
    const initialize = async () => {
      if (!currentUserId) {
        setIsInitialized(true);
        return;
      }
      try {
        const [locations, userProviders] = await Promise.all([
          locationService.getLocationsByRoleAccess(currentUserId),
          userProviderCalendarService.getUserProvidersByUserIdAsync(),
        ]);

        // Inactive providers are hidden from the agenda's booking dropdowns
        const sortedLocations = (locations ?? [])
          .map((location) => ({
            ...location,
            details: location.details?.filter((d) => d.isActive) ?? [],
          }))
          .sort((a, b) => a.locationName.localeCompare(b.locationName));
        setAccessibleLocations(sortedLocations);

        const ownProviderInfo = userProviders?.find((u) => u.userId === currentUserId);
        setIsProvider((ownProviderInfo?.details?.filter((d) => d.isActive).length ?? 0) > 0);

        const cachedIsValid =
          !!cachedLocationId &&
          sortedLocations.some((l) => l.locationId.toString() === cachedLocationId);
        setSelectedLocationId(
          cachedIsValid ? cachedLocationId : sortedLocations[0]?.locationId.toString() ?? ""
        );
      } catch {
        showErrorMessage("Error loading agenda");
      } finally {
        setIsInitialized(true);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);
    if (currentUserId) {
      dispatch(
        setLocationPageLocation({
          userId: currentUserId.toString(),
          selectedLocationId: locationId,
        })
      );
    }
  };

  const fetchMyEvents = useCallback(
    (startDate: string, endDate: string) =>
      currentUserId
        ? calendarEventService.getByUserLocationAsync(currentUserId, "", startDate, endDate)
        : Promise.resolve([]),
    [calendarEventService, currentUserId]
  );

  const fetchLocationEvents = useCallback(
    (startDate: string, endDate: string) =>
      selectedLocationId
        ? calendarEventService.getByLocationProviderAsync(selectedLocationId, "", startDate, endDate)
        : Promise.resolve([]),
    [calendarEventService, selectedLocationId]
  );

  const myAgenda = useAgendaEvents(
    isInitialized && isProvider && !!currentUserId,
    fetchMyEvents,
    myMode,
    myDate
  );
  const locationAgenda = useAgendaEvents(
    isInitialized && !!selectedLocationId,
    fetchLocationEvents,
    locationMode,
    locationDate
  );

  const refreshAgendas = () => {
    myAgenda.refresh();
    locationAgenda.refresh();
  };

  const dropdownResults = useMemo(
    () => accessibleLocations.flatMap((location) => location.details),
    [accessibleLocations]
  );

  const handleAppointmentSubmit = async (formEvent: any) => {
    if (!editingEvent) return;
    const values = formEvent.value;
    try {
      const update: UpdateCalendarEvent = {
        ...editingEvent,
        customerFirstName: values.customerFirstName,
        customerLastName: values.customerLastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        serviceId: values.serviceId,
        locationId: values.locationId,
        providerId: values.providerId,
        startDate: toYearMonthDayLocalString(values.start),
        startTime: formatTimeOnly(values.start) ?? "",
        endDate: toYearMonthDayLocalString(values.end),
        endTime: formatTimeOnly(values.end) ?? "",
        notes: values.notes,
      };
      const result = await calendarEventService.updateCalendarEventAsync(update);
      if (result.warning) {
        showWarningMessage(`Updated with warning: ${result.warning}`);
      } else {
        showSuccessMessage("Updated successfully.");
      }
      setEditingEvent(null);
      refreshAgendas();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "An unexpected error occurred.";
      showErrorMessage(errorMessage);
    }
  };

  const handleAppointmentDelete = async (dataItem: any) => {
    try {
      const result = await calendarEventService.deleteCalendarEventAsync(dataItem.bookingEventId);
      if (result) {
        showSuccessMessage("Event deleted successfully.");
      }
      refreshAgendas();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "An unexpected error occurred.";
      showErrorMessage(errorMessage);
    }
  };

  const editingDataItem = editingEvent
    ? {
        id: editingEvent.bookingEventId,
        bookingEventId: editingEvent.bookingEventId,
        customerFirstName: editingEvent.customerFirstName,
        customerLastName: editingEvent.customerLastName,
        email: editingEvent.email,
        phoneNumber: editingEvent.phoneNumber,
        locationId: editingEvent.locationId,
        providerId: editingEvent.providerId,
        providerName: editingEvent.providerName,
        serviceId: editingEvent.serviceId,
        start: getEventStart(editingEvent),
        end: getEventEnd(editingEvent),
        notes: editingEvent.notes,
      }
    : null;

  const locationOptions = useMemo(
    () =>
      accessibleLocations.map((location) => ({
        value: location.locationId.toString(),
        text: location.locationName,
      })),
    [accessibleLocations]
  );

  const locationPicker =
    accessibleLocations.length > 1 ? (
      <div className="agenda-location-picker">
        <SearchableLocationDropdown
          name="agendaLocation"
          locations={locationOptions}
          value={selectedLocationId}
          onChange={(e) => e.value?.value && handleLocationChange(e.value.value)}
        />
      </div>
    ) : (
      <span className="agenda-location-label fw-semibold">
        {accessibleLocations[0]?.locationName ?? ""}
      </span>
    );

  return (
    <div className="row w-100">
      {isProvider && (
        <div className="col col-24 col-lg-12 px-0">
          <AgendaCard
            title="My Agenda"
            mode={myMode}
            selectedDate={myDate}
            onModeChange={setMyMode}
            onDateChange={setMyDate}
            events={myAgenda.events}
            isLoading={!isInitialized || myAgenda.isLoading}
            now={myAgenda.now}
            detailLine="location"
            onEventClick={setEditingEvent}
            footerTo={routes.calendar.index}
            footerLabel="View My Calendar"
          />
        </div>
      )}
      <div className={`col col-24 ${isProvider ? "col-lg-12" : ""} px-0`}>
        <AgendaCard
          title="Location Agenda"
          mode={locationMode}
          selectedDate={locationDate}
          onModeChange={setLocationMode}
          onDateChange={setLocationDate}
          toolbarRight={locationPicker}
          events={locationAgenda.events}
          isLoading={!isInitialized || locationAgenda.isLoading}
          now={locationAgenda.now}
          detailLine="provider"
          onEventClick={setEditingEvent}
          footerTo={routes.calendar.location}
          footerLabel="View Location Calendar"
        />
      </div>
      {editingEvent && editingDataItem && (
        <CalendarBookingModal
          key={editingEvent.bookingEventId}
          {...({} as any)}
          dataItem={editingDataItem}
          dropdownResults={dropdownResults}
          onSubmit={handleAppointmentSubmit as any}
          onCancel={() => setEditingEvent(null)}
          onDeleteAppointment={handleAppointmentDelete}
        />
      )}
    </div>
  );
}

export default AgendaDashboard;
